import { useEffect, useLayoutEffect, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { getDevicePrefs } from "../app/devicePrefs";
import "./touchKeyboard.css";

// Kinmantle's own on-screen keyboard. A wall touchscreen (Chromium in kiosk
// mode on the Pi) has no system keyboard, so tapping a text box would leave no
// way to type. It opens when a field is tapped by touch; phones, tablets and
// Chromebooks keep their own keyboard unless "Always" is chosen in Display.
// The layout is a full desktop keyboard rather than a phone's: a wall screen
// is wide enough for every key at a comfortable size, typed with fingers of
// both hands, with no symbol pages to hunt through.

type Field = HTMLInputElement | HTMLTextAreaElement;
type Layout = "text" | "email" | "url" | "number";
type Shift = "off" | "once" | "lock";

const TEXT_TYPES = new Set(["text", "search", "email", "url", "tel", "password", "number"]);
const HAS_SYSTEM_KEYBOARD =
  typeof navigator !== "undefined" &&
  (/Android|iPhone|iPad|iPod|CrOS/i.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1));

function isField(el: EventTarget | null): el is Field {
  if (el instanceof HTMLTextAreaElement) return !el.readOnly && !el.disabled;
  if (el instanceof HTMLInputElement) return TEXT_TYPES.has(el.type) && !el.readOnly && !el.disabled;
  return false;
}

// Each field's own inputmode while ours is set to "none" (see claim below).
const savedMode = new WeakMap<Field, string | null>();

function layoutFor(el: Field): Layout {
  if (el instanceof HTMLInputElement) {
    const mode = savedMode.has(el) ? savedMode.get(el) : el.inputMode;
    if (el.type === "number" || el.type === "tel" || mode === "numeric" || mode === "decimal" || mode === "tel") return "number";
    if (el.type === "email" || mode === "email") return "email";
    if (el.type === "url" || mode === "url") return "url";
  }
  return "text";
}

// Email and number inputs have no selection API; edits go at the end there.
function selection(el: Field): [number, number] | null {
  try {
    return el.selectionStart === null || el.selectionEnd === null ? null : [el.selectionStart, el.selectionEnd];
  } catch {
    return null;
  }
}

/** Edit the field the way typing would, so React's onChange sees it. */
function edit(el: Field, text: string, back = false) {
  const value = el.value;
  const sel = selection(el);
  let [start, end] = sel ?? [value.length, value.length];
  if (back && start === end) {
    if (start === 0) return;
    start -= /[\uDC00-\uDFFF]/.test(value[start - 1] ?? "") && start > 1 ? 2 : 1;
  }
  const next = value.slice(0, start) + text + value.slice(end);
  if (el.maxLength >= 0 && next.length > el.maxLength && !back) return;
  // The prototype's setter bypasses React's value tracker, so the input event below registers as a change.
  Object.getOwnPropertyDescriptor(Object.getPrototypeOf(el), "value")?.set?.call(el, next);
  if (sel) el.setSelectionRange(start + text.length, start + text.length);
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

function moveCaret(el: Field, by: number) {
  const sel = selection(el);
  if (!sel) return;
  const at = Math.max(0, Math.min(el.value.length, (by < 0 ? sel[0] : sel[1]) + by));
  el.setSelectionRange(at, at);
}

function enter(el: Field) {
  if (el instanceof HTMLTextAreaElement) return edit(el, "\n");
  const init = { key: "Enter", code: "Enter", bubbles: true, cancelable: true };
  const unhandled = el.dispatchEvent(new KeyboardEvent("keydown", init));
  el.dispatchEvent(new KeyboardEvent("keyup", init));
  if (unhandled && el.form) el.form.requestSubmit();
}

// Capitalise the start of a sentence in ordinary text fields.
function wantsCapital(el: Field): boolean {
  const cap = el.getAttribute("autocapitalize");
  if (cap === "off" || cap === "none") return false;
  if (layoutFor(el) !== "text" || (el instanceof HTMLInputElement && el.type !== "text" && el.type !== "search")) return false;
  const before = el.value.slice(0, selection(el)?.[0] ?? el.value.length);
  return before.trim() === "" || /[.!?]\s$/.test(before);
}

/** The next (or previous) text box on the same sheet or page, for Tab. */
function neighbour(el: Field, dir: 1 | -1): Field | undefined {
  const scope = el.closest(".sheet") ?? document;
  const fields = [...scope.querySelectorAll("input, textarea")].filter((f): f is Field => isField(f) && (f as HTMLElement).offsetParent !== null);
  return fields[fields.indexOf(el) + dir];
}

// Rows in key units. Every row of the full keyboard is 15 units wide, so the
// rows stagger like a physical keyboard's. "x:1.5" is a key 1.5 units wide;
// specials are written as {name}.
const FULL = [
  "` 1 2 3 4 5 6 7 8 9 0 - = {back}:2",
  "{tab}:1.5 q w e r t y u i o p [ ] \\:1.5",
  "{caps}:1.75 a s d f g h j k l ; ' {enter}:2.25",
  "{shift}:2.25 z x c v b n m , . / {shift}:2.75",
];
const BOTTOM: Record<Exclude<Layout, "number">, string> = {
  text: "{hide}:2 {space}:10.5 {left}:1.25 {right}:1.25",
  email: "{hide}:2 @:1.25 {space}:7.5 .com:1.75 {left}:1.25 {right}:1.25",
  url: "{hide}:2 /:1.25 {space}:7.5 .com:1.75 {left}:1.25 {right}:1.25",
};
const KEYPAD = ["1 2 3 {back}", "4 5 6 -", "7 8 9 .", "{hide} 0 {enter}:2"];
const SHIFTED: Record<string, string> = {
  "`": "~", "1": "!", "2": "@", "3": "#", "4": "$", "5": "%", "6": "^", "7": "&", "8": "*", "9": "(", "0": ")",
  "-": "_", "=": "+", "[": "{", "]": "}", "\\": "|", ";": ":", "'": '"', ",": "<", ".": ">", "/": "?",
};

const parse = (row: string) =>
  row.split(" ").map((tok) => {
    const at = tok.lastIndexOf(":");
    return at > 0 ? { name: tok.slice(0, at), units: Number(tok.slice(at + 1)) } : { name: tok, units: 1 };
  });

export function TouchKeyboard() {
  const [field, setField] = useState<Field | null>(null);
  const [shift, setShift] = useState<Shift>("off");
  const fieldRef = useRef<Field | null>(null);
  fieldRef.current = field;
  const lastShiftTap = useRef(0);
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let lastTouch = 0;
    const mode = () => getDevicePrefs().touchKeyboard;
    const wanted = () => mode() === "on" || (mode() === "auto" && !HAS_SYSTEM_KEYBOARD && performance.now() - lastTouch < 1000);
    // inputmode="none" keeps any system keyboard from opening on top of ours.
    const claim = (el: Field) => {
      if (!savedMode.has(el)) savedMode.set(el, el.getAttribute("inputmode"));
      el.setAttribute("inputmode", "none");
    };
    const release = (el: Field) => {
      if (!savedMode.has(el)) return;
      const was = savedMode.get(el);
      if (was === null || was === undefined) el.removeAttribute("inputmode");
      else el.setAttribute("inputmode", was);
      savedMode.delete(el);
    };
    const open = (el: Field) => {
      claim(el);
      // Set now, not on the next render: a sheet that opens with an autofocused
      // field moves focus to itself straight away, and focusout must see this field.
      fieldRef.current = el;
      setField(el);
      // The caret lands where the field was tapped only after focus.
      requestAnimationFrame(() => setShift(wantsCapital(el) ? "once" : "off"));
    };

    const onPointerDown = (e: PointerEvent) => {
      lastTouch = e.pointerType === "touch" ? performance.now() : 0;
      if (isField(e.target) && wanted()) {
        claim(e.target);
        // Tapping the field again after hiding the keyboard brings it back.
        if (document.activeElement === e.target && fieldRef.current !== e.target) open(e.target);
      }
    };
    const onFocusIn = (e: FocusEvent) => {
      if (isField(e.target) && wanted()) open(e.target);
    };
    const onFocusOut = (e: FocusEvent) => {
      if (!isField(e.target)) return;
      release(e.target);
      // Moving straight to another field keeps the keyboard up instead of closing and reopening it.
      if (e.target === fieldRef.current && !(isField(e.relatedTarget) && wanted())) setField(null);
    };
    // Someone typing on a real keyboard doesn't need ours.
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.isTrusted && fieldRef.current && e.key !== "Escape") {
        lastTouch = 0;
        setField(null);
      }
    };
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("focusout", onFocusOut);
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("focusout", onFocusOut);
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, []);

  // Make room: sheets and pages pad by the keyboard's height, and the field scrolls into view.
  useLayoutEffect(() => {
    const doc = document.documentElement;
    const el = root.current;
    if (!field || !el) return;
    const apply = () => doc.style.setProperty("--osk-h", `${el.offsetHeight}px`);
    apply();
    doc.classList.add("osk-open");
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    // Only a field the keyboard covers (or off-screen) is scrolled: one in plain view stays where it is.
    const frame = requestAnimationFrame(() => {
      const r = field.getBoundingClientRect();
      if (r.top < 0 || r.bottom > window.innerHeight - el.offsetHeight) field.scrollIntoView({ block: "center", behavior: "smooth" });
    });
    return () => {
      ro.disconnect();
      cancelAnimationFrame(frame);
      doc.classList.remove("osk-open");
      doc.style.removeProperty("--osk-h");
    };
  }, [field]);

  if (!field) return null;
  const layout = layoutFor(field);

  const press = (key: string) => {
    const el = fieldRef.current;
    if (!el) return;
    switch (key) {
      case "{back}":
        edit(el, "", true);
        break;
      case "{left}":
        return moveCaret(el, -1);
      case "{right}":
        return moveCaret(el, 1);
      case "{enter}":
        return enter(el);
      case "{hide}":
        // Close even if the field has already lost focus (blur alone would do nothing then).
        el.blur();
        fieldRef.current = null;
        return setField(null);
      case "{tab}": {
        const to = neighbour(el, shift === "once" ? -1 : 1);
        if (shift === "once") setShift("off");
        if (!to) {
          el.blur();
          fieldRef.current = null;
          return setField(null);
        }
        to.focus();
        if (selection(to)) to.setSelectionRange(to.value.length, to.value.length);
        return;
      }
      case "{space}":
        edit(el, " ");
        break;
      case "{caps}":
        return setShift(shift === "lock" ? "off" : "lock");
      case "{shift}": {
        const now = performance.now();
        const double = now - lastShiftTap.current < 350;
        lastShiftTap.current = now;
        setShift(shift === "lock" ? "off" : double ? "lock" : shift === "once" ? "off" : "once");
        return;
      }
      default:
        edit(el, typed(key, shift));
        if (shift === "once") setShift("off");
    }
    if (shift !== "lock" && layout === "text" && wantsCapital(el)) setShift("once");
  };

  const rows = layout === "number" ? KEYPAD : [...FULL, BOTTOM[layout]];
  return createPortal(
    <div
      ref={root}
      className={`osk osk-${layout === "number" ? "keypad" : "full"}`}
      role="group"
      aria-label="On-screen keyboard"
      // Keys never take focus from the field being typed into.
      onPointerDown={(e) => e.preventDefault()}
      onMouseDown={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      {rows.map((row, r) => (
        <div className="osk-row" key={r}>
          {parse(row).map((k, i) => (
            <Key key={i} name={k.name} units={k.units} shift={shift} enterHint={field.enterKeyHint} onPress={press} />
          ))}
        </div>
      ))}
    </div>,
    document.body,
  );
}

/** What a character key types: Shift gives the upper legend; Caps Lock only capitals. */
function typed(key: string, shift: Shift): string {
  if (key.length !== 1) return key;
  if (key.toLowerCase() !== key.toUpperCase()) return shift === "off" ? key : key.toUpperCase();
  return shift === "once" ? (SHIFTED[key] ?? key) : key;
}

const LABELS: Record<string, ReactNode> = {
  "{back}": "⌫",
  "{left}": "←",
  "{right}": "→",
  "{space}": "",
  "{tab}": "Tab",
  "{caps}": "Caps",
  "{shift}": "⇧ Shift",
};
const ARIA: Record<string, string> = {
  "{back}": "Backspace",
  "{left}": "Move left",
  "{right}": "Move right",
  "{space}": "Space",
  "{tab}": "Next box",
  "{caps}": "Caps lock",
  "{shift}": "Shift",
  "{enter}": "Enter",
  "{hide}": "Hide keyboard",
};
const REPEATS = new Set(["{back}", "{left}", "{right}"]);

function Key({ name, units, shift, enterHint, onPress }: { name: string; units: number; shift: Shift; enterHint: string; onPress: (key: string) => void }) {
  const timers = useRef<number[]>([]);
  const stop = () => {
    // Timeouts and intervals share one id pool, so clearTimeout ends either.
    timers.current.forEach((t) => clearTimeout(t));
    timers.current = [];
  };
  useEffect(() => stop, []);

  const down = (e: ReactPointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture?.(e.pointerId);
    onPress(name);
    // Holding backspace or an arrow keeps going.
    if (REPEATS.has(name)) timers.current.push(window.setTimeout(() => timers.current.push(window.setInterval(() => onPress(name), 70)), 450));
  };

  const special = name.startsWith("{") && name.length > 1;
  const alt = SHIFTED[name];
  let label: ReactNode = LABELS[name] ?? typed(name, shift === "once" ? "lock" : shift);
  // Number and punctuation keys show both legends, like a real keycap.
  if (alt) {
    label = (
      <>
        <span className={`osk-alt ${shift === "once" ? "on" : ""}`}>{alt}</span>
        <span className={shift === "once" ? "osk-dim" : ""}>{name}</span>
      </>
    );
  }
  if (name === "{enter}") label = { go: "Go", done: "Done", search: "Search", send: "Send", next: "Next" }[enterHint] ?? "Enter ↵";
  if (name === "{hide}") label = <HideGlyph />;
  const on = (name === "{shift}" && shift !== "off") || (name === "{caps}" && shift === "lock");

  return (
    <button
      type="button"
      tabIndex={-1}
      className={`osk-key ${special ? `osk-${name.slice(1, -1)}` : ""} ${alt ? "osk-dual" : ""} ${on ? "on" : ""}`}
      style={{ flexGrow: units }}
      aria-label={ARIA[name] ?? name}
      onPointerDown={down}
      onPointerUp={stop}
      onPointerCancel={stop}
      onLostPointerCapture={stop}
    >
      {label}
    </button>
  );
}

function HideGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="1.4em" height="1.4em" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <rect x="3" y="4" width="18" height="11" rx="2" />
      <path d="M7 8h.01M11 8h.01M15 8h.01M8 11.5h8M9 19l3 2.5 3-2.5" />
    </svg>
  );
}
